// Onglet « Propriétaires et quotes-parts » de la fiche logement (F-006).
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { put } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Notice, Select, Table, fieldError, useToast } from "@core/ui";
import { equalShares, milliLabel, toMilli } from "./calc";

type Member = {
  id: string;
  full_name: string;
  role_label: string;
  share_label: string | null;
  mandate_status: string;
  effective_powers: { sign_leases: boolean; collect_rents: boolean; order_works: boolean };
  receives_reports: boolean;
};

type OwnerShare = {
  owner_id: string;
  kind: string;
  kind_label: string;
  display_name: string;
  legal_name: string | null;
  siren: string | null;
  share_milli: number;
  share_pct: number;
  share_label: string;
  archived: boolean;
  members: Member[];
};

type Row = { owner_id: string; share: string };

export function powersLabel(p: Member["effective_powers"]): string {
  const out = [p.sign_leases ? "signer les baux" : "", p.collect_rents ? "encaisser les loyers" : "", p.order_works ? "commander des travaux" : ""].filter(Boolean);
  return out.length ? `Peut ${out.join(", ")}` : "Aucun pouvoir déclaré";
}

export function OwnersTab({ propertyId, canEdit }: { propertyId: string; canEdit: boolean }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const detail = useGet<{ owners: OwnerShare[] }>(org.api(`/properties/${propertyId}`));
  const all = useGet<{ items: { id: string; display_name: string; kind_label: string; archived_at: string | null }[] }>(org.api("/owners"));
  const owners = detail.data?.owners ?? [];
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!editing) setRows(owners.map((o) => ({ owner_id: o.owner_id, share: (o.share_milli / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 3, useGrouping: false }) })));
  }, [editing, detail.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = rows.reduce((acc, r) => acc + (toMilli(r.share) ?? 0), 0);
  const options = (all.data?.items ?? []).filter((o) => !o.archived_at).map((o) => ({ value: o.id, label: `${o.display_name} (${o.kind_label})` }));

  if (detail.isLoading) return <Loading />;
  return (
    <div className="stack">
      <Notice>
        Le propriétaire juridique (personne, SCI, indivision, société) est distinct des membres de cet espace qui agissent dans Bailivo. Une personne ajoutée comme associé n’obtient aucun pouvoir sans que vous le déclariez.
      </Notice>
      {!editing ? (
        <Card
          title="Détention du bien"
          actions={
            canEdit ? (
              <Button small variant="secondaire" onClick={() => setEditing(true)}>
                Modifier les quotes-parts
              </Button>
            ) : null
          }
        >
          {owners.length === 0 ? (
            <Empty title="Aucun propriétaire renseigné.">Indiquez qui détient ce bien et dans quelle proportion. Créez d’abord le propriétaire dans « Propriétaires » si besoin.</Empty>
          ) : (
            <div className="stack">
              {owners.map((o) => (
                <div key={o.owner_id} className="stack-sm">
                  <div className="row-between">
                    <div>
                      <Link to={org.to(`proprietaires/${o.owner_id}`)}>
                        <strong>{o.display_name}</strong>
                      </Link>
                      <span className="pat-sub">
                        {o.kind_label}
                        {o.siren ? ` · SIREN ${o.siren}` : ""}
                        {o.archived ? " · archivé" : ""}
                      </span>
                    </div>
                    <Badge tone="encre">{o.share_label}</Badge>
                  </div>
                  {o.members.length ? (
                    <Table caption={`Associés et représentants de ${o.display_name}`}>
                      <thead>
                        <tr>
                          <th>Personne</th>
                          <th>Qualité</th>
                          <th>Part</th>
                          <th>Pouvoirs effectifs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.members.map((m) => (
                          <tr key={m.id}>
                            <td>
                              {m.full_name}
                              {m.receives_reports ? <span className="pat-sub">Reçoit les rapports</span> : null}
                            </td>
                            <td>{m.role_label}</td>
                            <td className="num">{m.share_label ?? "—"}</td>
                            <td className="small">
                              {m.mandate_status === "expire" ? "Mandat expiré : aucun pouvoir effectif" : m.mandate_status === "a_venir" ? "Mandat pas encore en vigueur" : powersLabel(m.effective_powers)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : o.kind !== "personne" ? (
                    <p className="small muted">Aucun associé ni représentant renseigné pour ce propriétaire.</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : (
        <Card title="Modifier les quotes-parts">
          <ErrorBox error={error && !fieldError(error, "owners") ? error : null} />
          {fieldError(error, "owners") ? <Notice tone="erreur">{fieldError(error, "owners")}</Notice> : null}
          {options.length === 0 ? (
            <p>
              Aucun propriétaire dans cet espace. <Link to={org.to("proprietaires")}>Créer un propriétaire</Link>
            </p>
          ) : null}
          {rows.map((r, i) => (
            <div key={i} className="pat-share-row">
              <Select
                label={`Propriétaire ${i + 1}`}
                value={r.owner_id}
                options={[{ value: "", label: "Choisissez" }, ...options]}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, owner_id: e.target.value } : x)))}
              />
              <Input label="Quote-part (%)" inputMode="decimal" value={r.share} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} />
              <Button variant="discret" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={`Retirer le propriétaire ${i + 1}`}>
                Retirer
              </Button>
            </div>
          ))}
          <div className="row">
            <Button small variant="secondaire" onClick={() => setRows([...rows, { owner_id: "", share: "" }])}>
              Ajouter un propriétaire
            </Button>
            {rows.length > 1 ? (
              <Button small variant="discret" onClick={() => setRows(rows.map((r, i) => ({ ...r, share: equalShares(rows.length)[i] })))}>
                Répartir à parts égales
              </Button>
            ) : null}
          </div>
          <p>
            Total : <span className="pat-total">{milliLabel(total)}</span>{" "}
            {rows.length === 0 ? null : total === 100000 ? <Badge tone="succes">Répartition complète</Badge> : <Badge tone="attention">Le total doit faire exactement 100 %</Badge>}
          </p>
          <div className="form-actions">
            <Button variant="discret" onClick={() => setEditing(false)}>
              Annuler
            </Button>
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const body = { owners: rows.filter((r) => r.owner_id).map((r) => ({ owner_id: r.owner_id, share_pct: (toMilli(r.share) ?? 0) / 1000 })) };
                  await put(org.api(`/properties/${propertyId}/owners`), body);
                  invalidate(org.api(`/properties`), org.api("/owners"));
                  toast("Quotes-parts enregistrées.");
                  setEditing(false);
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Enregistrer
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
